import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	OnInit,
	output,
	signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	CentsToDollarsPipe,
	DestroyService,
	DialogService,
	InfiniteScrollBaseComponent,
	PercentToValuePipe,
} from '@marmon/frontend/common';
import {
	Analysis,
	AnalysisCalculation,
	InvoiceRollup,
	InvoiceRollupAverage,
	PercentileGroup,
	RollupEntity,
} from '@marmon/shared/types/analysis';
import { PageSize, SortMod } from '@marmon/shared/types/pagination';
import { QuadEightyTwentyRange } from '@marmon/shared/types/quad';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiLink } from '@taiga-ui/core';
import { TuiSkeleton } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { limit, where } from 'firebase/firestore';
import { map, of, switchMap, takeUntil, tap } from 'rxjs';
import { TopCountEntityCalculationDialogComponent } from '../../dialogs';
import { IsCostTypePipe } from '../../pipes';
import { AnalysisApiService, AnalysisStateService } from '../../services';
import {
	AverageTransactionCellComponent,
	MarginPercentEightyCellComponent,
	MarginPercentTotalCellComponent,
	MarginPercentTwentyCellComponent,
	PercentOfEightyCellComponent,
} from '../color-code-cell';

type AdditionalOptions = {
	calculation: AnalysisCalculation | null;
} | null;

const Limit = 10;

@Component({
	selector: 'mar-top-count-entity-table',
	templateUrl: 'top-count-entity-table.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		TuiTable,
		TranslocoPipe,
		AsyncPipe,
		CentsToDollarsPipe,
		TuiSkeleton,
		PercentToValuePipe,
		MarginPercentTotalCellComponent,
		PercentOfEightyCellComponent,
		MarginPercentEightyCellComponent,
		MarginPercentTwentyCellComponent,
		AverageTransactionCellComponent,
		IsCostTypePipe,
		TuiLink,
	],
	providers: [DestroyService],
})
export class TopCountEntityTableComponent
	extends InfiniteScrollBaseComponent<InvoiceRollup, AdditionalOptions>
	implements OnInit
{
	readonly entity = input.required<RollupEntity>();
	readonly appearance = input<'primary' | 'secondary'>('primary');

	readonly translation = computed(() => `customer-product.${this.entity()}`);

	override columnKeys: string[] = [];

	override readonly disableInfiniteScroll = true;
	readonly analysisApiService = inject(AnalysisApiService);
	readonly analysisStateService = inject(AnalysisStateService);
	readonly dialog = inject(DialogService);

	readonly percentiles = signal<PercentileGroup | null>(null);

	readonly analysis$ = this.analysisStateService.analysis$;
	readonly calculation$ = this.analysisStateService.calculation$
		.pipe(
			takeUntil(this.destroy$),
			tap((calculation) => this.additional$.next({ calculation })),
		)
		.subscribe();

	readonly infoDialog = (analysis: Analysis) =>
		this.dialog.open(
			new PolymorpheusComponent(TopCountEntityCalculationDialogComponent),
			TopCountEntityCalculationDialogComponent.Options({
				data: { costType: analysis.costType },
			}),
		);

	readonly rollupAverage = signal<InvoiceRollupAverage | null>(null);

	readonly loaded = output<void>();

	override request$ = (
		options: [
			PageSize,
			keyof InvoiceRollup | null,
			SortMod,
			AdditionalOptions,
			void,
		],
	) =>
		of(options).pipe(
			takeUntil(this.destroy$),
			switchMap((options) => {
				const analysis = this.analysisStateService.analysis;
				if (!analysis) return Promise.resolve(null);

				const additional = options[3];
				const { calculation } = additional ?? {};
				if (!calculation) return Promise.resolve(null);

				return Promise.all([
					this.analysisApiService
						.findCalculationEntityAverage({
							analysisId: analysis.id,
							calculationId: calculation.id,
							averageType: `${this.entity()}s80`,
						})
						.then((average) => {
							this.rollupAverage.set(average);
						}),
					this.analysisApiService
						.findCalculationPercentile({
							analysisId: analysis.id,
							calculationId: calculation.id,
							grouping: `${this.entity()}s80`,
						})
						.then((percentiles) =>
							this.percentiles.set(percentiles),
						),
					this.analysisApiService.fetch(
						this.paginatedObject.update({
							sortBy: 'revenueInCents',
							pageSize: Limit,
							sortDir: 'desc',
							constraints: [
								limit(Limit),
								where(
									'quad',
									'<=',
									QuadEightyTwentyRange.Eighty,
								),
							],
						}),
						{
							subcollections: [
								{ docId: analysis.id, name: 'calculations' },
								{
									docId: calculation.id,
									name: `${this.entity()}s`,
								},
							],
							next: this.loadingMore,
						},
					),
				]);
			}),
			tap(() => {
				this.loaded.emit();
			}),
			map(() => this.paginatedObject),
		);

	ngOnInit() {
		this.columnKeys = determineColumnKeys(this.entity());
	}

	onInfo() {
		const analysis = this.analysisStateService.analysis;
		if (!analysis) return;
		this.infoDialog(analysis).subscribe();
	}
}

/**
 * Determines what the column keys should be based on the options provided
 *
 * @param entity the entity to generate the keys for
 */
const determineColumnKeys = (entity: RollupEntity) => [
	'index',
	entity,
	'revenueInCents',
	'count80',
	'count20',
	'percent80',
	'transactionCount',
	'transactionAvgInCents',
	'marginPercent80',
	'marginPercent20',
	'marginPercent',
];
